import { useState } from 'react';
import { toast } from 'sonner';
import { Archive, ArchiveRestore, Layers, Pencil, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { PERMISSIONS } from '@/client/config/permissions';
import { useGate } from '@/client/hooks/useGate';
import {
  useDeleteLevelMutation,
  useHrEmployeesQuery,
  useHrLevelsQuery,
  useUpsertLevelMutation,
} from '@/client/api/hr/people';
import { hasLeft, LEVEL_TRACK_LABEL, type HrLevel, type LevelTrack } from '@/client/types/hr';
import { DeleteConfirmDialog, EmptyState, RowMenu } from '../../HumanResource/components/hr-ui';
import { BusinessPageShell } from '../components/BusinessPageShell';
import { BusinessTable, Td } from '../components/BusinessTable';
import { StatusBadge } from '../../HumanResource/components/hr-ui';
import { money } from '../../HumanResource/components/comp-format';

const inputClass =
  'h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm outline-none ring-blue-500 focus:ring-2';

type Form = { code: string; name: string; rank: string; track: LevelTrack; currency: string; min: string; max: string };
const EMPTY: Form = { code: '', name: '', rank: '1', track: 'ic', currency: 'GHS', min: '', max: '' };

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium text-gray-600">{label}</span>
      {children}
    </label>
  );
}

export default function LevelsPage() {
  const levelsQ = useHrLevelsQuery();
  const employees = useHrEmployeesQuery().data ?? [];
  const upsert = useUpsertLevelMutation();
  const remove = useDeleteLevelMutation();
  const canManage = useGate(PERMISSIONS.hr.manage).allowed;
  const [editing, setEditing] = useState<HrLevel | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<Form>(EMPTY);
  const [pending, setPending] = useState<{ level: HrLevel; kind: 'delete' | 'retire' | 'restore' } | null>(null);

  const levels = levelsQ.data ?? [];
  const headcount = (id: string) => employees.filter((e) => e.levelId === id && !hasLeft(e)).length;

  const openForm = (l: HrLevel | null) => {
    setEditing(l);
    setForm(
      l
        ? {
            code: l.code,
            name: l.name,
            rank: String(l.rank),
            track: l.track,
            currency: l.compensationBand?.currency ?? 'GHS',
            min: l.compensationBand ? String(l.compensationBand.minMonthly) : '',
            max: l.compensationBand ? String(l.compensationBand.maxMonthly) : '',
          }
        : { ...EMPTY, rank: String((levels.at(-1)?.rank ?? 0) + 1) },
    );
    setOpen(true);
  };

  const failed = (what: string) => (err: unknown) => toast.error((err as Error).message || `Could not ${what}`);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.code.trim() || !form.name.trim()) {
      toast.error('Code and name are required');
      return;
    }
    const hasBand = form.min.trim() !== '' || form.max.trim() !== '';
    upsert.mutate(
      {
        id: editing?.id,
        code: form.code.trim(),
        name: form.name.trim(),
        rank: Number(form.rank) || 0,
        track: form.track,
        compensationBand: hasBand
          ? { currency: form.currency.trim().toUpperCase(), minMonthly: Number(form.min), maxMonthly: Number(form.max) }
          : null,
      },
      {
        onSuccess: () => {
          toast.success(editing ? 'Level updated' : 'Level added');
          setOpen(false);
        },
        onError: failed('save the level'),
      },
    );
  };

  const confirm = () => {
    if (!pending) return;
    const { level, kind } = pending;
    if (kind === 'delete') {
      remove.mutate(level.id, { onSuccess: () => toast.success(`${level.code} deleted`), onError: failed('delete the level') });
    } else {
      upsert.mutate(
        { id: level.id, status: kind === 'retire' ? 'retired' : 'active' },
        { onSuccess: () => toast.success(kind === 'retire' ? `${level.code} retired` : `${level.code} restored`), onError: failed('update the level') },
      );
    }
    setPending(null);
  };

  const copy =
    pending?.kind === 'delete'
      ? { title: 'Delete level?', body: `"${pending.level.code} ${pending.level.name}" will be deleted. A level someone holds cannot be deleted; retire it instead.` }
      : pending?.kind === 'retire'
        ? { title: 'Retire level?', body: `"${pending.level.code}" stays on the people who hold it, but can no longer be picked for anyone else.` }
        : { title: 'Restore level?', body: `"${pending?.level.code}" can be picked again.` };

  return (
    <BusinessPageShell
      title="Levels"
      description="Seniority levels and their monthly pay bands. Raises are checked against the band."
      crumbs={[{ label: 'People', href: '/admin/human-resource' }, { label: 'Levels' }]}
      icon={Layers}
      mock={false}
      actions={
        <PermissionButton code={PERMISSIONS.hr.manage} className="gap-2" onClick={() => openForm(null)}>
          <Plus className="h-4 w-4" /> Add level
        </PermissionButton>
      }
      stats={[
        { label: 'Levels', value: String(levels.filter((l) => l.status === 'active').length) },
        { label: 'With a pay band', value: String(levels.filter((l) => l.compensationBand).length) },
        { label: 'People with no level', value: String(employees.filter((e) => !hasLeft(e) && !e.levelId).length) },
      ]}
    >
      {levelsQ.isError ? (
        <EmptyState label={`Could not load levels: ${(levelsQ.error as Error)?.message ?? 'unknown error'}`} />
      ) : levelsQ.isLoading ? (
        <EmptyState label="Loading levels…" />
      ) : levels.length === 0 ? (
        <EmptyState label="No levels yet. Add the first one." />
      ) : (
        <BusinessTable headers={['Code', 'Name', 'Track', 'Rank', 'Pay band (monthly)', 'Status', 'Headcount', '']}>
          {levels.map((l) => (
            <tr key={l.id} className={canManage ? 'cursor-pointer hover:bg-gray-50' : 'hover:bg-gray-50'} onClick={() => canManage && openForm(l)}>
              <Td className="font-medium text-gray-900">{l.code}</Td>
              <Td>{l.name}</Td>
              <Td>{LEVEL_TRACK_LABEL[l.track]}</Td>
              <Td>{l.rank}</Td>
              <Td>
                {l.compensationBand
                  ? `${money(l.compensationBand.minMonthly, l.compensationBand.currency)} – ${money(l.compensationBand.maxMonthly, l.compensationBand.currency)}`
                  : '—'}
              </Td>
              <Td>
                <StatusBadge label={l.status} className={`capitalize ${l.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`} />
              </Td>
              <Td>{headcount(l.id)}</Td>
              <Td className="text-right">
                <RowMenu
                  label={`Actions for ${l.code}`}
                  items={[
                    { label: 'Edit', icon: Pencil, onSelect: () => openForm(l), hidden: !canManage },
                    l.status === 'active'
                      ? { label: 'Retire', icon: Archive, onSelect: () => setPending({ level: l, kind: 'retire' }), hidden: !canManage }
                      : { label: 'Restore', icon: ArchiveRestore, onSelect: () => setPending({ level: l, kind: 'restore' }), hidden: !canManage },
                    { label: 'Delete', icon: Trash2, onSelect: () => setPending({ level: l, kind: 'delete' }), danger: true, hidden: !canManage },
                  ]}
                />
              </Td>
            </tr>
          ))}
        </BusinessTable>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit level' : 'Add level'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Code">
                <input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} className={inputClass} placeholder="L3" />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Name">
                  <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputClass} placeholder="Senior" />
                </Field>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Track">
                <select value={form.track} onChange={(e) => setForm((f) => ({ ...f, track: e.target.value as LevelTrack }))} className={inputClass}>
                  {Object.entries(LEVEL_TRACK_LABEL).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Rank (higher is more senior)">
                <input type="number" value={form.rank} onChange={(e) => setForm((f) => ({ ...f, rank: e.target.value }))} className={inputClass} />
              </Field>
            </div>
            <div>
              <p className="mb-1.5 text-xs font-medium text-gray-600">Monthly pay band (optional)</p>
              <div className="grid gap-3 sm:grid-cols-3">
                <input
                  value={form.currency}
                  onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
                  className={inputClass}
                  maxLength={3}
                  aria-label="Currency"
                />
                <input
                  type="number"
                  min={0}
                  value={form.min}
                  onChange={(e) => setForm((f) => ({ ...f, min: e.target.value }))}
                  className={inputClass}
                  placeholder="Minimum"
                  aria-label="Band minimum"
                />
                <input
                  type="number"
                  min={0}
                  value={form.max}
                  onChange={(e) => setForm((f) => ({ ...f, max: e.target.value }))}
                  className={inputClass}
                  placeholder="Maximum"
                  aria-label="Band maximum"
                />
              </div>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={upsert.isPending}>
                {upsert.isPending ? 'Saving…' : editing ? 'Save changes' : 'Add level'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={!!pending}
        onOpenChange={(v) => !v && setPending(null)}
        title={copy.title}
        description={copy.body}
        onConfirm={confirm}
        actionLabel={pending?.kind === 'delete' ? 'Delete' : pending?.kind === 'retire' ? 'Retire' : 'Restore'}
        danger={pending?.kind !== 'restore'}
      />
    </BusinessPageShell>
  );
}
